import { useContext, useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import SlotSelector from './SlotSelector';
import AppointmentDaySelector from './AppointmentDaySelector';
import { UserContext } from '../../../context/UserContext';
import MobileDateTimePicker from '../../../components/atoms/MobileDateTimePicker';
import ReactDatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "../../../react-datetime-picker.css";
import { addHours, isBefore, setMinutes, setHours, format } from "date-fns";
import { toast } from 'react-toastify';

const Schedule = ({ state, dispatch }) => {
  const { permission } = useContext(UserContext);
  const graceTime = state.services.includes('taxi') ? permission.PAW_TAXI_BOOKING_INTERVAL : permission.PAW_WALK_BOOKING_INTERVAL;

  const [minDateTime, setMinDateTime] = useState(addHours(new Date(), 3));
  const [selectedDateTime, setSelectedDateTime] = useState(state.scheduled_at ? new Date(state.scheduled_at) : null);
  const [isMobile, setIsMobile] = useState(false);

  // Detect if device is mobile
  useEffect(() => {
    const checkIsMobile = () => {
      const userAgent = navigator.userAgent || navigator.vendor || window.opera;
      const mobileRegex = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i;
      const isMobileDevice = mobileRegex.test(userAgent.toLowerCase()) || window.innerWidth <= 768;
      setIsMobile(isMobileDevice);
    };

    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);
    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);

  const handleDateChange = (date) => {
    if (!date) return;

    const nowPlusThree = addHours(new Date(), 3);
    if (isBefore(date, nowPlusThree)) {
      toast.error("Please select a time at least 3 hours ahead.");
      return;
    }

    setSelectedDateTime(date);

    const formattedDate = format(date, "yyyy-MM-dd'T'HH:mm"); // ✅ matches backend

    dispatch({
      type: 'UPDATE_FIELD',
      field: 'scheduled_at',
      payload: formattedDate, // ✅ send this, NOT toISOString
    });
  };


  const renderDateTimePicker = () => (
    <div className="mt-6 w-full">
      <label className="block text-lg font-medium text-gray-700 mb-2">
        Select Date & Time
      </label>
      <div className="w-full">
        {isMobile ? (
          <MobileDateTimePicker
            selectedDateTime={selectedDateTime}
            onChange={handleDateChange}
            minHoursAhead={3}
            minTime={6}
            maxTime={21}
            placeholder="Pick a future date & time"
          />
        ) : (
          <ReactDatePicker
            selected={selectedDateTime}
            onChange={handleDateChange}
            showTimeSelect
            timeIntervals={30}
            minDate={new Date()}
            minTime={setHours(setMinutes(new Date(), 0), 6)}
            maxTime={setHours(setMinutes(new Date(), 0), 21)}
            dateFormat="MMMM d, yyyy h:mm aa"
            placeholderText="Pick a future date & time"
            popperPlacement="bottom-start"
            wrapperClassName="w-full"
            className="w-full block px-4 py-3 text-sm border border-purple-300 rounded-xl bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
          />
        )}
        <p className="text-xs text-gray-500 mt-2 italic">
          * Bookings must be at least 3 hour ahead and between 6 AM – 9 PM.
        </p>
      </div>
    </div>
  );



  return (
    <div className="p-2 sm:p-4">
      {state.services.includes('taxi') && (
        <motion.div
          className="service-card  rounded-lg  sm:p-6 mb-6 sm:mb-8"
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <div className="space-y-4 sm:space-y-6">

            <h2 className="text-lg sm:text-xl md:text-2xl font-semibold text-purple-700 text-center mb-3 sm:mb-4">
              Hop On Our Paw Taxi! 🚖🐾
            </h2>

            <div className="text-center space-y-2 sm:space-y-3 mb-6 sm:mb-8">
              <p className="text-sm sm:text-base text-gray-600">
                Choose how you'd like to schedule your pet's taxi:
                {/* <span className="font-bold text-purple-700 mx-1">Slot</span> or */}
                <span className="font-bold text-purple-700 mx-1">Custom Time.</span>
              </p>
              <p className="text-purple-700 font-bold">OR</p>
              <p className="text-sm sm:text-base text-gray-600 mt-2">
                Want an <span className="font-bold text-purple-700">Instant Taxi?</span>
                <span className="block mt-2 text-xs sm:text-sm">
                  Simply press <span className="bg-primary rounded px-2 py-1 text-white">Next</span> to skip scheduling!
                </span>
              </p>
            </div>

            {/* Scheduling Options */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 max-w-2xl mx-auto">
              {/* {['instant', 'slot', 'datetime'].map((type) => ( */}
              {['instant', 'datetime'].map((type) => (
                <div
                  key={type}
                  onClick={() => dispatch({ type: "UPDATE_FIELD", field: "scheduleType", payload: type })}
                  className={`
                    flex items-center justify-start p-3 sm:p-4 
                    bg-white rounded-lg border-2 
                    ${state.scheduleType === type
                      ? 'border-purple-500 bg-purple-50'
                      : 'border-gray-200 hover:border-purple-300'}
                    cursor-pointer transition-all duration-200
                    shadow-sm hover:shadow-md
                  `}
                >
                  <input
                    type="radio"
                    name="scheduleType"
                    value={type}
                    checked={state.scheduleType === type}
                    onChange={() => { }}
                    className="form-radio h-4 w-4 sm:h-5 sm:w-5 text-purple-600"
                  />
                  <label className="ml-2 sm:ml-3 text-sm sm:text-base text-gray-800 cursor-pointer">
                    {type === 'datetime' ? 'Pick Date & Time' : type.charAt(0).toUpperCase() + type.slice(1)}
                  </label>
                </div>
              ))}
            </div>

            {/* Slot Selection */}
            {state.scheduleType === "slot" && (
              <div className="mt-6 sm:mt-8">
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.5 }}
                  className="space-y-4 sm:space-y-6"
                >
                  <AppointmentDaySelector state={state} dispatch={dispatch} />

                  {graceTime > 0 && state.day && (
                    <div className="bg-purple-50 border-l-4 border-purple-500 p-3 sm:p-4 rounded-r-lg">
                      <p className="text-xs sm:text-sm text-gray-700">
                        <strong>Note:</strong> We include a {graceTime / 2}-minute grace period before and after.
                        <span className="block mt-1 text-gray-600 italic text-xs">
                          This additional time is not billed.
                        </span>
                      </p>
                    </div>
                  )}

                  {state.day && <SlotSelector state={state} dispatch={dispatch} serviceType="taxi" />}
                </motion.div>
              </div>
            )}

            {/* DateTime Selection */}
            {state.scheduleType === 'datetime' && renderDateTimePicker()}

          </div>
        </motion.div>
      )
      }

      {/* Walk Service Section */}
      {
        state.services.includes('walk') && (
          <motion.div
            className="walk-service"
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <div className="mb-12">

              <h2 className="text-lg sm:text-xl md:text-2xl font-semibold text-purple-700 text-center mb-3 sm:mb-4">
                Ready for a Paw-some Adventure? 🐾
              </h2>

              <div className="text-center space-y-2 sm:space-y-3 mb-6 sm:mb-8">
                <p className="text-sm sm:text-base text-gray-600">
                  Choose how you'd like to schedule your pet's walk:
                  {/* <span className="font-bold text-purple-700 mx-1">Slot</span> or */}
                  <span className="font-bold text-purple-700 mx-1">Custom Time.</span>
                </p>
                <p className="text-purple-700 font-bold">OR</p>
                <p className="text-sm sm:text-base text-gray-600 mt-2">
                  Want an <span className="font-bold text-purple-700">Instant Walk?</span>
                  <span className="block mt-2 text-xs sm:text-sm">
                    Simply press <span className="bg-primary rounded px-2 py-1 text-white">Next</span> to skip scheduling!
                  </span>
                </p>
              </div>

              {/* Scheduling Options */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 max-w-2xl mx-auto">
                {/* {['instant', 'slot', 'datetime'].map((type) => ( */}
                {['instant', 'datetime'].map((type) => (
                  <div
                    key={type}
                    onClick={() => dispatch({ type: "UPDATE_FIELD", field: "scheduleType", payload: type })}
                    className={`
                    flex items-center justify-start p-3 sm:p-4 
                    bg-white rounded-lg border-2 
                    ${state.scheduleType === type
                        ? 'border-purple-500 bg-purple-50'
                        : 'border-gray-200 hover:border-purple-300'}
                    cursor-pointer transition-all duration-200
                    shadow-sm hover:shadow-md
                  `}
                  >
                    <input
                      type="radio"
                      name="scheduleType"
                      value={type}
                      checked={state.scheduleType === type}
                      onChange={() => { }}
                      className="form-radio h-4 w-4 sm:h-5 sm:w-5 text-purple-600"
                    />
                    <label className="ml-2 sm:ml-3 text-sm sm:text-base text-gray-800 cursor-pointer">
                      {type === 'datetime' ? 'Pick Date & Time' : type.charAt(0).toUpperCase() + type.slice(1)}
                    </label>
                  </div>
                ))}
              </div>

              {/* If Slot Scheduling is Selected */}
              {state.scheduleType === "slot" && (
                <div className="mt-12">
                  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }}>
                    <AppointmentDaySelector state={state} dispatch={dispatch} />
                    {graceTime > 0 && state.day && (
                      <div className="mt-4 p-3 bg-white border-l-4 border-purple-600">
                        <p className="text-gray-700 text-sm">
                          <strong>Note:</strong> To ensure a seamless experience, we include a complimentary {graceTime / 2}-minute grace period before and after the scheduled walk.
                          This allows for any unexpected delays and ensures a smooth transition for both your pet and the walker.
                          <span className="block mt-1 text-gray-600 italic">This additional time is not billed.</span>
                        </p>
                      </div>
                    )}
                    {state.day && <SlotSelector state={state} dispatch={dispatch} serviceType="walk" />}
                  </motion.div>
                </div>
              )}

              {/* If DateTime Scheduling is Selected */}
              {state.scheduleType === 'datetime' && renderDateTimePicker()}

            </div>
          </motion.div>
        )
      }
    </div >
  );
};

export default Schedule;